import { useTranslation } from '@crew/modules/i18n'
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  TaskTemplateTaskListAndEntryForm,
  TaskTemplateTaskListAndEntryFormHandle,
  TreeListTaskTemplateTask,
} from './components/taskTemplateTaskListAndEntryForm/taskTemplateTaskListAndEntryForm'

import { useGetTaskTemplateQuery } from '@crew/apis/taskTemplate/taskTemplateApis'
import { GetTaskTemplateRequest } from '@crew/apis/taskTemplate/models/getTaskTemplate/request'
import { CrewTextBoxField } from 'components/forms/crewTextBoxField'
import {
  FormValues,
  useTaskTemplateEntryForm,
} from './useTaskTemplateEntryForm'
import { CrewRadioGroupField } from 'components/forms/crewRadioGroupField'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { useModal } from 'components/layouts/modal/useModal'
import { skipToken } from '@reduxjs/toolkit/query'
import { useValueChangeEffect } from '@crew/hooks'

export type TaskTemplateEntryFormProps = {
  isEditMode: boolean
  projectId: string
  taskTemplateId?: string
  initialTaskTemplateTasks?: TreeListTaskTemplateTask[]
  onSubmit: () => void
  onDeleted: () => void
  onCancel: () => void
}

export const TaskTemplateEntryForm: FC<TaskTemplateEntryFormProps> = memo(
  (props) => {
    const { t } = useTranslation()
    const [showApiErrors] = useShowApiErrors()
    const toast = useToast()
    const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
      useModal()

    // 確認ダイアログメッセージ
    const [confirmMessage, setConfirmMessage] = useState('')

    const {
      control,
      handleSubmit,
      validateRules,
      reset,
      formState,
      insertTaskTemplate,
      isLoadingInsertTaskTemplate,
      updateTaskTemplate,
      isLoadingUpdateTaskTemplate,
      deleteTaskTemplate,
      isLoadingDeleteTaskTemplate,
      taskTemplateDataSource,
    } = useTaskTemplateEntryForm()

    // タスク一覧のバリデーション結果
    const [hasTaskListValidationErrors, setHasTaskListValidationErrors] =
      useState(false)

    const canSend = useMemo(
      // fromState.isValidはerrorsが空でもfalseになることがあるためerrorsで判定する
      () =>
        Object.keys(formState.errors).length === 0 &&
        !formState.isSubmitting &&
        !hasTaskListValidationErrors,
      // formStateはproxyなのでformState自体をlistenする必要がある
      // https://react-hook-form.com/api/useform/formstate
      // taskTemplateTasksとバリデーション結果の変更も監視
      [formState, hasTaskListValidationErrors]
    )

    const getTaskTemplateParam: GetTaskTemplateRequest | undefined =
      props.isEditMode && props.taskTemplateId
        ? {
            taskTemplateId: props.taskTemplateId,
          }
        : undefined

    const { data: getTaskTemplateResult } = useGetTaskTemplateQuery(
      getTaskTemplateParam ?? skipToken
    )

    const [taskTemplateTasks, setTaskTemplateTasks] = useState<
      TreeListTaskTemplateTask[]
    >([])

    const [version, setVersion] = useState<number | undefined>(undefined)

    // TaskTemplateTaskListAndEntryFormへの参照
    const taskTemplateTaskListAndEntryFormRef =
      useRef<TaskTemplateTaskListAndEntryFormHandle>(null)

    // 初期化処理
    useEffect(() => {
      if (props.isEditMode && props.taskTemplateId) {
        // 編集モード時は既存データのセットを行う
        const init = async () => {
          if (!props.taskTemplateId) return

          if (!getTaskTemplateResult) return

          // 取得したデータをセット
          // taskOrderをベースにIDとparentTaskTemplateTaskIdを変換
          const convertedTasks: TreeListTaskTemplateTask[] =
            getTaskTemplateResult.taskTemplate.taskTemplateTasks.map((task) => {
              // 親タスクのtaskOrderを探してparentTaskTemplateTaskOrderを設定
              // ツリーリストではtaskOrderをキーに処理するため
              let newParentTaskTemplateTaskOrder: string | null = null
              if (task.parentTaskTemplateTaskId) {
                const parentTask =
                  getTaskTemplateResult.taskTemplate.taskTemplateTasks.find(
                    (t) => t.id === task.parentTaskTemplateTaskId
                  )
                if (parentTask) {
                  newParentTaskTemplateTaskOrder = parentTask.taskOrder
                }
              }

              return {
                taskOrder: task.taskOrder,
                parentTaskTemplateTaskOrder: newParentTaskTemplateTaskOrder,
                subject: task.subject,
                taskKindId: task.taskKindId,
                taskKind: task.taskKind,
                taskPriority: task.taskPriority,
                assignToUserId: task.assignToUserId,
                assignToUser: task.assignToUser,
                requiredDays: task.requiredDays,
                description: task.description ?? '',
              }
            })

          // taskOrder昇順で並べ替えてからセット（1,1-1,2,...の順で表示されるようにする）
          const sortedTasks = [...convertedTasks].sort((a, b) =>
            a.taskOrder.localeCompare(b.taskOrder, undefined, {
              numeric: true,
            })
          )

          // templateTaskをローカルステートにセット
          setTaskTemplateTasks(sortedTasks)

          // タスクテンプレート本体の情報をセット
          reset({
            name: getTaskTemplateResult.taskTemplate.name,
            scope: getTaskTemplateResult.taskTemplate.scope,
          })

          setVersion(getTaskTemplateResult.taskTemplate.version)
        }

        init()
      } else if (!props.isEditMode) {
        // 登録モードでは初期タスクを反映（未指定/空ならクリア）
        setTaskTemplateTasks(props.initialTaskTemplateTasks ?? [])
      }
    }, [
      props.isEditMode,
      props.taskTemplateId,
      reset,
      getTaskTemplateResult,
      props.initialTaskTemplateTasks,
    ])

    // タスク一覧が変更された際にバリデーション結果を更新
    useValueChangeEffect(
      () => {
        const hasErrors =
          taskTemplateTaskListAndEntryFormRef.current?.hasValidationErrors() ??
          false
        setHasTaskListValidationErrors(hasErrors)
      },
      [],
      taskTemplateTasks
    )

    const dialogClose = useCallback(() => {
      props.onCancel()
    }, [props])

    // ハンドラ

    // 登録・更新ボタン
    const handleSubmitButtonClick = useCallback(() => {
      const onSubmit = async (
        data: FormValues,
        currentTaskTemplateTasks: TreeListTaskTemplateTask[]
      ) => {
        try {
          if (props.isEditMode) {
            if (!props.taskTemplateId || version === undefined) return

            await updateTaskTemplate(
              props.taskTemplateId,
              version,
              data,
              currentTaskTemplateTasks
            )

            dialogClose()

            props.onSubmit()

            toast.success(t('message.taskTemplate.taskTemplateUpdated'))
          } else {
            await insertTaskTemplate(
              data,
              currentTaskTemplateTasks,
              props.projectId
            )

            dialogClose()

            props.onSubmit()

            toast.success(t('message.taskTemplate.taskTemplateRegistered'))
          }
        } catch (err) {
          showApiErrors(err)
        }
      }

      // 子コンポーネントから現在のフォーム値を取得
      // 行でのフォーカスアウトがstate変更のイベントなので、最後に編集したものはstateに反映されないため
      const currentTaskTemplateFormValues =
        taskTemplateTaskListAndEntryFormRef.current?.getCurrentFormValues()

      let currentTaskTemplateTasks = taskTemplateTasks

      // currentTaskTemplateがnullではない場合、該当タスクを更新する
      if (currentTaskTemplateFormValues) {
        const focusedRowKey = currentTaskTemplateFormValues.key

        currentTaskTemplateTasks = taskTemplateTasks.map((task) => {
          if (task.taskOrder === focusedRowKey) {
            return {
              ...task,
              subject: currentTaskTemplateFormValues.subject,
              taskKindId: currentTaskTemplateFormValues.taskKindId,
              taskPriority: currentTaskTemplateFormValues.taskPriority,
              assignToUserId: currentTaskTemplateFormValues.assignToUserId,
              requiredDays: currentTaskTemplateFormValues.requiredDays,
              description: currentTaskTemplateFormValues.description,
            }
          }
          return task
        })
      }

      // UI更新のためにstate側も更新
      setTaskTemplateTasks(currentTaskTemplateTasks)

      handleSubmit((data) => onSubmit(data, currentTaskTemplateTasks))()
    }, [
      dialogClose,
      handleSubmit,
      insertTaskTemplate,
      props,
      showApiErrors,
      t,
      taskTemplateTasks,
      toast,
      updateTaskTemplate,
      version,
    ])

    // 削除ボタン
    const handleDeleteButtonClick = useCallback(() => {
      setConfirmMessage(t('message.general.confirmMessage.delete'))

      openConfirmDialog()
    }, [openConfirmDialog, t])

    // 削除確認ダイアログ OKボタン
    const handleDeletePermitButtonClick = useCallback(async () => {
      if (props.isEditMode && props.taskTemplateId) {
        try {
          await deleteTaskTemplate(props.taskTemplateId)

          toast.success(t('message.taskTemplate.taskTemplateDeleted'))

          dialogClose()

          // 削除後は呼び出し元でページ遷移などを行う
          if (props.onDeleted) {
            props.onDeleted()
            return
          }
        } catch (err) {
          showApiErrors(err)
        }
      }
      closeConfirmDialog()
    }, [
      closeConfirmDialog,
      deleteTaskTemplate,
      dialogClose,
      props,
      showApiErrors,
      t,
      toast,
    ])

    return (
      <>
        <div className="flex flex-col h-full gap-y-2.5">
          <div className="flex flex-row gap-x-2.5">
            {/** テンプレート名 */}
            <div className="w-2/5">
              <CrewTextBoxField
                id="name"
                name="name"
                control={control}
                labelMode="hidden"
                label={t('label.taskTemplate.templateName')}
                required
                width="100%"
                rules={validateRules.name}
              />
            </div>
            {/** 公開範囲 */}
            <CrewRadioGroupField
              control={control}
              id="scope"
              name="scope"
              label={t('label.scope')}
              rules={validateRules.scope}
              required
              dataSource={taskTemplateDataSource}
              valueExpr="id"
              displayExpr="name"
              layout="horizontal"
            />
          </div>

          {/** タスクテンプレートタスク編集フォーム（リスト, フォーム部分） */}
          <TaskTemplateTaskListAndEntryForm
            ref={taskTemplateTaskListAndEntryFormRef}
            projectId={props.projectId}
            taskTemplateTasks={taskTemplateTasks}
            setTaskTemplateTasks={(
              taskTemplateTasks: TreeListTaskTemplateTask[]
            ) => {
              setTaskTemplateTasks(taskTemplateTasks)
            }}
          />

          {/** 区切り線 */}
          <div className="w-full h-1 border-b crew-border-gray" />

          <div className="flex flex-row gap-x-2.5">
            {props.isEditMode && (
              <CrewButton
                text={t('action.delete')}
                type="danger"
                disabled={isLoadingDeleteTaskTemplate}
                onClick={handleDeleteButtonClick}
              />
            )}
            <div className="flex flex-row ml-auto gap-x-2.5">
              <CrewButton
                text={t('action.register')}
                type="primary"
                disabled={
                  !canSend ||
                  isLoadingInsertTaskTemplate ||
                  isLoadingUpdateTaskTemplate
                }
                onClick={handleSubmitButtonClick}
              />
              <CrewButton
                text={t('action.cancel')}
                type="normal"
                stylingMode="outlined"
                onClick={dialogClose}
              />
            </div>
          </div>
        </div>
        {/* 削除確認ダイアログ */}
        <CrewConfirmDialog
          isOpen={isConfirmDialogOpen}
          message={confirmMessage}
          onPermitButtonClick={handleDeletePermitButtonClick}
          onCancelButtonClick={closeConfirmDialog}
          permitButtonDisabled={isLoadingDeleteTaskTemplate}
        />
      </>
    )
  }
)
